import React, { useEffect, useId, useMemo, useState } from 'react'
import Select from 'react-select'
import api from 'api/api'
import { panelSwipeExclusionProps } from 'components/shared/panelSwipeExclusions'
import { themedSettingsSelectStyles } from 'components/shared/reactSelectStyles'
import {
    FEATURE_FLAG_SORTS,
    filterAndSortFeatureFlags,
} from 'components/users/featureFlagSettingsUtils'
import styles from './profile.module.scss'

const FEATURE_FLAG_SORT_OPTIONS = [
    { value: FEATURE_FLAG_SORTS.RECENT, label: 'Newest first' },
    { value: FEATURE_FLAG_SORTS.ALPHABETICAL, label: 'Alphabetical' },
]

const findFeatureFlagSortOption = value =>
    FEATURE_FLAG_SORT_OPTIONS.find(option => option.value === value) || FEATURE_FLAG_SORT_OPTIONS[0]

export default function FeatureFlagSettings({ onToggle }) {
    const [isOpen, setIsOpen] = useState(false)
    const [features, setFeatures] = useState([])
    const [query, setQuery] = useState('')
    const [sort, setSort] = useState(
        /** @type {import('./featureFlagSettingsUtils').FeatureFlagSort} */ (
            FEATURE_FLAG_SORTS.RECENT
        ),
    )
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [togglingFeatures, setTogglingFeatures] = useState(() => new Set())
    const sortSelectId = useId()
    const selectMenuPortalTarget = typeof document === 'undefined' ? undefined : document.body

    const visibleFeatures = useMemo(
        () => filterAndSortFeatureFlags(features, query, sort),
        [features, query, sort],
    )

    useEffect(() => {
        if (!isOpen) return undefined

        const closeOnEscape = event => {
            if (event.key === 'Escape') setIsOpen(false)
        }
        document.addEventListener('keydown', closeOnEscape)
        return () => document.removeEventListener('keydown', closeOnEscape)
    }, [isOpen])

    const openPopup = async () => {
        setIsOpen(true)
        setLoading(true)
        setError('')

        const response = await api.getUserFeatures()
        if (response.features) {
            setFeatures(response.features)
        } else {
            setError(response.error || 'Failed to load feature flags')
        }
        setLoading(false)
    }

    const toggleFeature = async featureName => {
        setTogglingFeatures(current => new Set(current).add(featureName))
        setError('')

        const response = await onToggle(featureName)
        if (response?.features) {
            const enabledFeatureNames = new Set(response.features)
            setFeatures(current =>
                current.map(feature => ({
                    ...feature,
                    enabled: enabledFeatureNames.has(feature.name),
                })),
            )
        } else {
            setError(response?.error || `Failed to update ${featureName}`)
        }

        setTogglingFeatures(current => {
            const next = new Set(current)
            next.delete(featureName)
            return next
        })
    }

    return (
        <>
            <div className={`${styles.emailNotiContainer} ${styles.featureFlagEntry}`}>
                <div className={styles.emailNoti}>
                    <span className={styles.emailNotiLabel}>Feature Flags</span>
                    <div className={styles.emailNotiDescription}>
                        Enable or disable experimental features for your account.
                    </div>
                </div>
                <button className={styles.featureFlagButton} type="button" onClick={openPopup}>
                    Manage Features
                </button>
            </div>

            {isOpen && (
                <div
                    className={styles.featureFlagOverlay}
                    onMouseDown={event => {
                        if (event.target === event.currentTarget) setIsOpen(false)
                    }}
                >
                    <div
                        className={styles.featureFlagPopup}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="feature-flag-title"
                        {...panelSwipeExclusionProps}
                    >
                        <div className={styles.featureFlagHeader}>
                            <div>
                                <h2 id="feature-flag-title">Feature Flags</h2>
                                <p>Changes apply only to your account.</p>
                            </div>
                            <button
                                className={styles.closeButton}
                                type="button"
                                aria-label="Close feature flags"
                                onClick={() => setIsOpen(false)}
                            >
                                <i className="fa fa-remove" />
                            </button>
                        </div>

                        <div className={styles.featureFlagToolbar}>
                            <label className={styles.featureFlagSearch}>
                                <span className={styles.visuallyHidden}>Search feature flags</span>
                                <i className="fa fa-search" aria-hidden="true" />
                                <input
                                    autoFocus
                                    type="search"
                                    value={query}
                                    placeholder="Search features"
                                    onChange={event => setQuery(event.target.value)}
                                />
                            </label>
                            <div className={styles.featureFlagSort} {...panelSwipeExclusionProps}>
                                <label className={styles.visuallyHidden} htmlFor={sortSelectId}>
                                    Sort feature flags
                                </label>
                                <Select
                                    inputId={sortSelectId}
                                    value={findFeatureFlagSortOption(sort)}
                                    onChange={option => {
                                        if (option) setSort(option.value)
                                    }}
                                    options={FEATURE_FLAG_SORT_OPTIONS}
                                    styles={themedSettingsSelectStyles}
                                    menuPortalTarget={selectMenuPortalTarget}
                                    menuPosition="fixed"
                                    isSearchable={false}
                                    isClearable={false}
                                />
                            </div>
                        </div>

                        {!loading && !error && (
                            <div className={styles.featureFlagCount}>
                                {visibleFeatures.length} of {features.length} features
                            </div>
                        )}

                        <div className={styles.featureFlagList} aria-live="polite">
                            {loading && (
                                <div className={styles.featureFlagStatus}>
                                    <i className="fa fa-spinner fa-spin" /> Loading features…
                                </div>
                            )}
                            {!loading && error && (
                                <div className={styles.featureFlagError}>{error}</div>
                            )}
                            {!loading && !error && visibleFeatures.length === 0 && (
                                <div className={styles.featureFlagStatus}>
                                    No matching features found.
                                </div>
                            )}
                            {!loading &&
                                visibleFeatures.map(feature => {
                                    return (
                                        <div className={styles.featureFlagRow} key={feature.name}>
                                            <code>{feature.name}</code>
                                            <label className={styles.toggleSwitch}>
                                                <input
                                                    type="checkbox"
                                                    checked={feature.enabled}
                                                    disabled={togglingFeatures.size > 0}
                                                    aria-label={`${feature.name} feature`}
                                                    onChange={() => toggleFeature(feature.name)}
                                                />
                                                <span className={styles.toggleSlider}></span>
                                            </label>
                                        </div>
                                    )
                                })}
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
